import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import StoreIcon from "@mui/icons-material/Store";
import ScienceIcon from "@mui/icons-material/Science";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import BiotechIcon from "@mui/icons-material/Biotech";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  getLocalValue,
  LoacalVariables,
  updateMainHeader,
} from "../../../commonFunctions/CommonFunctions";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isOutcomeFilter, matchesOutcome, OUTCOME_FILTER_LABELS } from "../labOrderOutcome";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import http from "../../../commonFunctions/httpClient";
import WSDataGridNew, { IColumns } from "../../../Components/DataGrid/WSDataGridNew";
import { useVendorOrdersHub } from "../../../commonFunctions/vendorOrdersHub";
import { ingestOrders, isLabOrderWaiting, useNewOrderHighlights } from "../../../commonFunctions/newOrderHighlights";
import { AssignTechnicianDialog } from "./AssignTechnicianDialog";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import {
  formatAmount,
  formatDate,
  getStatusStyle,
  AcceptOrderButton,
  NewOrderTag,
  newOrderBlinkSx,
  ORDER_COLORS,
  orderFieldSx,
  orderFilterChipSx,
  StatusChip,
  toDateKey,
} from "../../../Components/Orders/OrderCardParts";

interface Patient {
  customerProfileId: string;
  name: string;
}

interface Service {
  testId: string;
  testName: string | null;
  rate: number;
  reportUrl: string;
}

interface LabBooking {
  bookingId: string;
  // Order number shown to users; the API may send it under any of these names
  orderNumber?: string | number | null;
  bookingNumber?: string | number | null;
  orderNo?: string | number | null;
  labId: string;
  labName: string;
  testId: string | null;
  testName: string | null;
  userId: string;
  username: string | null;
  patients: Patient[];
  slotId: string;
  slotTime: string;
  collectionType: string;
  address: {
    line1: string;
    line2: string;
    pinCode: string;
  } | null;
  labContact: string | null;
  paymentMode: string;
  paymentStatus: string;
  rate: number;
  collectionFee: number;
  serviceFee: number;
  totalAmount: number;
  status: string;
  services: Service[];
  scheduledDate: string;
  // When the order was placed, under whichever name the API sends it
  createdAt?: string | null;
  createdDate?: string | null;
  bookingDate?: string | null;
  bookedOn?: string | null;
}

// Placed time for ordering (falls back to the scheduled date when the API leaves it out)
const getPlacedTime = (order: LabBooking) => {
  const placed = order.createdAt || order.createdDate || order.bookingDate || order.bookedOn || order.scheduledDate;
  const time = new Date(placed).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// Cards shown per status section before "Show all"
const GROUP_PREVIEW = 6;

// Status sections follow a booking's journey; unknown statuses go after these, alphabetically
const STATUS_SEQUENCE = [
  ["pending"],
  ["confirmed"],
  ["assigned"],
  ["sample collected", "samplecollected"],
  ["in progress", "inprogress"],
  ["report ready", "reportready"],
  ["completed", "delivered", "fulfilled"],
  ["cancelled", "canceled", "rejected"],
  ["refund initiated", "refundinitiated", "refund", "refunded"],
];

const statusRank = (key: string) => {
  const index = STATUS_SEQUENCE.findIndex((group) => group.includes(key));
  return index === -1 ? STATUS_SEQUENCE.length : index;
};

const getStatusLabel = (order: LabBooking) => (order.status || order.paymentStatus || "No status").trim();

// Order History status: an order still waiting for a technician shows as "Not Assigned"
const NOT_ASSIGNED_LABEL = "Not Assigned";

const COLORS = ORDER_COLORS;

// Customer-facing order number; empty when the API doesn't send one
const getOrderNumber = (order: LabBooking) =>
  String(order.orderNumber || order.bookingNumber || order.orderNo || "").trim();

const getPatientNames = (order: LabBooking) =>
  order.patients?.map((p) => p.name).filter(Boolean).join(", ") || order.username || "N/A";

// The bookings API stores each booked test/package by its testId and may send testName as null;
// the names come from the lab's own tests and packages (lab/LabService/bylab)
const fillServiceNames = (bookings: LabBooking[], namesById: Map<string, string>): LabBooking[] => {
  if (namesById.size === 0) return bookings;
  const nameOf = (id: string | null | undefined) => (id ? namesById.get(String(id).toLowerCase()) || null : null);
  return bookings.map((booking) => ({
    ...booking,
    testName: booking.testName || nameOf(booking.testId),
    services: (booking.services || []).map((service) => ({
      ...service,
      testName: service.testName || nameOf(service.testId),
    })),
  }));
};

const getServiceNames = (order: LabBooking) => {
  const names = (order.services || []).map((s) => s.testName).filter(Boolean) as string[];
  if (names.length === 0 && order.testName) names.push(order.testName);
  return names;
};

// Orders that are assigned, being processed by a technician, or finished can't take a technician
const canAssignTechnician = (order: LabBooking) => isLabOrderWaiting(order);

// Today Orders keeps the orders still waiting for a technician (and today's ones in progress);
// Order History lists every order
const isHistoryOrder = (order: LabBooking) => !canAssignTechnician(order);

// A technician has been assigned and the order is still in progress
const ASSIGNED_KEY = "assigned";
const statusKey = (order: LabBooking) => (order.status || "").trim().toLowerCase();
const isAssignedOrder = (order: LabBooking) => statusKey(order) === ASSIGNED_KEY;

// The sample has been collected and the report is still to be submitted
const SAMPLE_COLLECTED_KEYS = ["sample collected", "samplecollected", "in progress", "inprogress", "inprocess"];
const isSampleCollectedOrder = (order: LabBooking) => SAMPLE_COLLECTED_KEYS.includes(statusKey(order));
// Status saved when the vendor marks the sample collected (the value the order timeline reads)
const SAMPLE_COLLECTED_STATUS = "SampleCollected";

// The report has been uploaded
const REPORT_SUBMITTED_KEYS = [
  "report generated", "reportgenerated", "report ready", "reportready",
  "report uploaded", "reportuploaded", "report submitted", "reportsubmitted",
];
const isReportSubmittedOrder = (order: LabBooking) => REPORT_SUBMITTED_KEYS.includes(statusKey(order));

// Today Orders status filters (orders still waiting for a technician are shown under All status)
const ORDER_STEPS = [
  { key: "assigned", label: "Assigned", matches: isAssignedOrder },
  { key: "sample-collected", label: "Sample Collected", matches: isSampleCollectedOrder },
  { key: "report-submitted", label: "Report Submitted", matches: isReportSubmittedOrder },
];

// Taken by a technician (up to the report being submitted): still listed on Today Orders
const isTechnicianStepOrder = (order: LabBooking) =>
  isAssignedOrder(order) || isSampleCollectedOrder(order) || isReportSubmittedOrder(order);

// Card action button (Assign technician / Sample collected / Submit report)
const CardActionButton: React.FC<{ icon: React.ReactNode; label: string; onClick: () => void; disabled?: boolean }> = ({
  icon,
  label,
  onClick,
  disabled,
}) => (
  <Box sx={{ px: 2, pb: 2 }}>
    <Button
      fullWidth
      variant="outlined"
      startIcon={icon}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        borderColor: COLORS.primary,
        color: COLORS.primary,
        borderRadius: "10px",
        textTransform: "none",
        fontWeight: 600,
        "&:hover": { bgcolor: COLORS.light, borderColor: COLORS.primary },
      }}
    >
      {label}
    </Button>
  </Box>
);

interface OrderCardProps {
  order: LabBooking;
  isNew: boolean;
  onView: () => void;
  onAssign: () => void;
  onAccept: () => void;
  onSampleCollected: () => void;
  onSubmitReport: () => void;
  // A status change for this order is being saved
  busy: boolean;
}

// Steps on a card: Accept → Assign technician → Sample collected → Submit report
const OrderCard: React.FC<OrderCardProps> = ({
  order,
  isNew,
  onView,
  onAssign,
  onAccept,
  onSampleCollected,
  onSubmitReport,
  busy,
}) => {
  // Fall back to the booking-level test when no services are attached
  const services: Partial<Service>[] = order.services?.length
    ? order.services
    : order.testName
      ? [{ testName: order.testName, rate: order.rate }]
      : [];
  const isHome = (order.collectionType || "").toLowerCase().includes("home");
  const address = order.address
    ? [order.address.line1, order.address.line2, order.address.pinCode].filter(Boolean).join(", ")
    : "";

  return (
    <Paper
      elevation={0}
      onClick={onView}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        border: `1px solid ${COLORS.border}`,
        overflow: "hidden",
        cursor: "pointer",
        transition: "box-shadow 0.2s, border-color 0.2s",
        "&:hover": { boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.08)", borderColor: COLORS.primary },
        ...(isNew ? newOrderBlinkSx : {}),
      }}
    >
      {/* Header: order number, date, status (the internal booking id is never shown) */}
      <Box sx={{ px: 2, py: 1.5, bgcolor: COLORS.light, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          {getOrderNumber(order) && (
            <Typography sx={{ fontWeight: 700, color: COLORS.dark, fontSize: "0.95rem" }} noWrap>
              #{getOrderNumber(order)}
            </Typography>
          )}
          <Typography
            sx={
              getOrderNumber(order)
                ? { color: COLORS.muted, fontSize: "0.75rem" }
                : { fontWeight: 700, color: COLORS.dark, fontSize: "0.95rem" }
            }
          >
            {formatDate(order.scheduledDate)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {isNew && <NewOrderTag />}
          <StatusChip label={order.status || order.paymentStatus} />
        </Box>
      </Box>

      <Box sx={{ px: 2, pt: 1.5, pb: 1, flexGrow: 1, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {/* Patient */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon sx={{ fontSize: 18, color: COLORS.primary }} />
          <Typography sx={{ fontWeight: 600, color: COLORS.dark, fontSize: "0.9rem" }} noWrap>
            {getPatientNames(order)}
          </Typography>
        </Box>

        {/* Tests / packages with rates */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {services.length === 0 ? (
            <Typography sx={{ color: COLORS.muted, fontSize: "0.85rem" }}>No tests listed</Typography>
          ) : (
            services
              .slice(0, 3)
              .map((service, index) => (
                <Box key={`${service.testId || index}`} sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
                    <ScienceIcon sx={{ fontSize: 16, color: COLORS.muted, mt: "2px" }} />
                    <Typography sx={{ color: COLORS.dark, fontSize: "0.85rem" }}>
                      {service.testName || "Test"}
                    </Typography>
                  </Box>
                  {service.rate ? (
                    <Typography sx={{ color: COLORS.muted, fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                      {formatAmount(service.rate)}
                    </Typography>
                  ) : null}
                </Box>
              ))
          )}
          {services.length > 3 && (
            <Typography sx={{ color: COLORS.primary, fontSize: "0.8rem", fontWeight: 600, pl: 3 }}>
              +{services.length - 3} more
            </Typography>
          )}
        </Box>

        {/* Slot + collection type */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          <Chip
            size="small"
            icon={isHome ? <HomeIcon sx={{ fontSize: 16 }} /> : <StoreIcon sx={{ fontSize: 16 }} />}
            label={order.collectionType || "N/A"}
            variant="outlined"
            sx={{ borderColor: COLORS.border, color: COLORS.dark, "& .MuiChip-icon": { color: COLORS.primary } }}
          />
          {order.slotTime && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <ScheduleIcon sx={{ fontSize: 16, color: COLORS.muted }} />
              <Typography sx={{ color: COLORS.muted, fontSize: "0.8rem" }}>{order.slotTime}</Typography>
            </Box>
          )}
        </Box>

        {/* Address for home collection */}
        {isHome && address && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <LocationOnIcon sx={{ fontSize: 16, color: COLORS.muted, mt: "2px" }} />
            <Typography
              sx={{
                color: COLORS.muted,
                fontSize: "0.8rem",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {address}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer: total, payment, action */}
      <Divider sx={{ borderStyle: "dashed", borderColor: COLORS.border, mx: 2 }} />
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: COLORS.dark, fontSize: "1rem" }}>
            {formatAmount(order.totalAmount)}
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem" }}>
            {[order.paymentMode, order.paymentStatus].filter(Boolean).join(" · ") || "-"}
          </Typography>
        </Box>
        <Button
          size="small"
          endIcon={<ChevronRightIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          sx={{ color: COLORS.primary, fontWeight: 600, textTransform: "none" }}
        >
          View details
        </Button>
      </Box>
      {isNew ? (
        // A new order is accepted first; then the button below takes its place
        <Box sx={{ px: 2, pb: 2 }}>
          <AcceptOrderButton onAccept={onAccept} />
        </Box>
      ) : canAssignTechnician(order) ? (
        <CardActionButton icon={<PersonAddAlt1Icon />} label="Assign technician" onClick={onAssign} />
      ) : isAssignedOrder(order) ? (
        <CardActionButton
          icon={<BiotechIcon />}
          label={busy ? "Saving..." : "Sample collected"}
          onClick={onSampleCollected}
          disabled={busy}
        />
      ) : isSampleCollectedOrder(order) ? (
        <CardActionButton icon={<UploadFileIcon />} label="Submit report" onClick={onSubmitReport} />
      ) : null}
    </Paper>
  );
};

interface OrdersSectionProps {
  id: string;
  title: string;
  orders: LabBooking[];
  emptyText: string;
  onView: (order: LabBooking) => void;
  onAssign: (order: LabBooking) => void;
  onSampleCollected: (order: LabBooking) => void;
  onSubmitReport: (order: LabBooking) => void;
  // Booking ids whose status change is being saved
  busyIds: string[];
  // Filter bar (Order History); the Orders page shows cards only
  showFilters?: boolean;
  // Show every card in a status group instead of the first few with "Show all"
  showAllCards?: boolean;
  // Table listing instead of cards (Order History)
  tableView?: boolean;
}

// One list (open orders or history), cards grouped by status
const OrdersSection: React.FC<OrdersSectionProps> = ({
  id,
  title,
  orders,
  emptyText,
  onView,
  onAssign,
  onSampleCollected,
  onSubmitReport,
  busyIds,
  showFilters = false,
  showAllCards = false,
  tableView = false,
}) => {
  const { isNew, accept } = useNewOrderHighlights();
  // Today Orders (cards) has a step filter line; the choice lives in the page address
  // (?status=sample-collected), so opening an order and coming back keeps it
  const filterable = !showFilters && !tableView;
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = (searchParams.get("status") || "").trim().toLowerCase();
  const activeStep = filterable ? ORDER_STEPS.find((step) => step.key === statusParam) : undefined;
  const statusFilter = activeStep ? activeStep.key : "All";
  const setStatusFilter = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "All") next.delete("status");
    else next.set("status", value);
    // The collection type filter was removed; drop it from old links
    next.delete("type");
    setSearchParams(next, { replace: true });
  };
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const paymentOptions = useMemo(() => {
    const statuses = Array.from(new Set(orders.map((o) => o.paymentStatus).filter(Boolean)));
    return ["All", ...statuses];
  }, [orders]);

  const baseFiltered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = orders.filter((order) => {
      if (paymentFilter !== "All" && order.paymentStatus !== paymentFilter) return false;
      const dateKey = toDateKey(order.scheduledDate);
      if (fromDate && (!dateKey || dateKey < fromDate)) return false;
      if (toDate && (!dateKey || dateKey > toDate)) return false;
      if (term) {
        const haystack = [getOrderNumber(order), getPatientNames(order), ...getServiceNames(order)].join(" ").toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
    // Orders arrive newest first
    return sortOrder === "oldest" ? [...result].reverse() : result;
  }, [orders, search, paymentFilter, fromDate, toDate, sortOrder]);

  // One group per status (case-insensitive), in journey order; cards keep the chosen sort inside a group
  const allGroups = useMemo(() => {
    const groups = new Map<string, { key: string; label: string; orders: LabBooking[] }>();
    baseFiltered.forEach((order) => {
      const label = getStatusLabel(order);
      const key = label.toLowerCase();
      if (!groups.has(key)) groups.set(key, { key, label, orders: [] });
      groups.get(key)!.orders.push(order);
    });
    return Array.from(groups.values()).sort(
      (a, b) => statusRank(a.key) - statusRank(b.key) || a.label.localeCompare(b.label)
    );
  }, [baseFiltered]);

  // Today Orders "All status" is one list, so a card stays where it is while it moves through its
  // steps (Accept → Assign technician → Sample collected → Submit report); only its button changes.
  // A step filter shows just the orders at that step, in the same order.
  const stepOrders = activeStep ? baseFiltered.filter(activeStep.matches) : baseFiltered;
  const visibleGroups = !filterable
    ? allGroups
    : stepOrders.length
      ? [{ key: activeStep?.key || "all", label: activeStep?.label || "All orders", orders: stepOrders }]
      : [];
  const visibleCount = visibleGroups.reduce((sum, group) => sum + group.orders.length, 0);

  // Step filter buttons, always shown with their counts
  const stepChips = useMemo(
    () => ORDER_STEPS.map((step) => ({ key: step.key, label: step.label, count: baseFiltered.filter(step.matches).length })),
    [baseFiltered]
  );

  const hasFilters = search || paymentFilter !== "All" || fromDate || toDate || statusFilter !== "All";

  const clearFilters = () => {
    setSearch("");
    setPaymentFilter("All");
    setFromDate("");
    setToDate("");
    if (filterable) {
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      next.delete("type");
      setSearchParams(next, { replace: true });
    }
  };

  // Table rows for Order History, in the chosen sort order
  const tableRows = useMemo(
    () =>
      baseFiltered.map((order) => ({
        id: order.bookingId,
        date: formatDate(order.scheduledDate),
        slot: order.slotTime || "-",
        patient: getPatientNames(order),
        tests: getServiceNames(order).join(", ") || "-",
        collectionType: order.collectionType || "-",
        amount: formatAmount(order.totalAmount),
        payment: [order.paymentMode, order.paymentStatus].filter(Boolean).join(" · ") || "-",
        status: canAssignTechnician(order) ? NOT_ASSIGNED_LABEL : getStatusLabel(order),
        actions: "",
        raw: order,
      })),
    [baseFiltered]
  );

  const tableColumns: IColumns[] = [
    { header: "Date", fieldName: "date", type: "default" },
    { header: "Slot", fieldName: "slot", type: "default" },
    { header: "Patient", fieldName: "patient", type: "default" },
    { header: "Test / Package", fieldName: "tests", type: "default" },
    { header: "Collection", fieldName: "collectionType", type: "default", isTitleCase: true },
    { header: "Amount", fieldName: "amount", type: "default" },
    { header: "Payment", fieldName: "payment", type: "default" },
    { header: "Status", fieldName: "status", type: "status" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["View"],
      actionCallback: (action, row) => {
        if (action === "View") onView(row.raw);
      },
    },
  ];

  const toggleGroup = (key: string) => setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Box component="section" id={id} aria-labelledby={`${id}-title`} sx={{ scrollMarginTop: 80 }}>
      {/* Section title + count */}
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 1.5, px: 0.5 }}>
        <Typography id={`${id}-title`} component="h2" sx={{ fontWeight: 700, color: COLORS.dark, fontSize: "1.15rem" }}>
          {title}{" "}
          <Box component="span" sx={{ color: COLORS.muted, fontWeight: 500, fontSize: "0.9rem" }}>
            {filterable ? visibleCount : hasFilters ? `${visibleCount} of ${orders.length}` : orders.length}
          </Box>
        </Typography>
        {hasFilters && (
          <Button size="small" onClick={clearFilters} sx={{ color: COLORS.primary, textTransform: "none" }}>
            Clear filters
          </Button>
        )}
      </Box>

      {/* Today Orders filter line: one button per step; click again to clear the choice */}
      {filterable && (
        <Box
          role="group"
          aria-label="Filter orders"
          sx={{ display: "flex", alignItems: "center", gap: 1, overflowX: "auto", mb: 2, pb: 0.5 }}
        >
          <Chip
            label={`All status · ${baseFiltered.length}`}
            aria-pressed={statusFilter === "All"}
            onClick={() => setStatusFilter("All")}
            sx={{ ...orderFilterChipSx(statusFilter === "All"), flexShrink: 0 }}
          />
          {stepChips.map((chip) => (
            <Chip
              key={chip.key}
              label={`${chip.label} · ${chip.count}`}
              aria-pressed={statusFilter === chip.key}
              onClick={() => setStatusFilter(statusFilter === chip.key ? "All" : chip.key)}
              sx={{ ...orderFilterChipSx(statusFilter === chip.key), flexShrink: 0 }}
            />
          ))}
        </Box>
      )}

      {/* Filters */}
      {showFilters && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: "16px" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by order ID, patient or test"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                inputProps={{ "aria-label": `Search ${title.toLowerCase()}` }}
                sx={orderFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: COLORS.muted }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: toDate || undefined }}
                sx={orderFieldSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDate || undefined }}
                sx={orderFieldSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Payment"
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                sx={orderFieldSx}
              >
                {paymentOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "All" ? "All payments" : option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Sort"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as "newest" | "oldest")}
                sx={orderFieldSx}
              >
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      )}

      {visibleCount === 0 ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", textAlign: "center" }}>
          <Typography sx={{ color: COLORS.dark, fontWeight: 600 }}>No orders found</Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: "0.875rem", mt: 0.5 }}>
            {hasFilters ? "Try changing or clearing the filters." : emptyText}
          </Typography>
        </Paper>
      ) : tableView ? (
        // Remount on filter change so the table goes back to page 1
        <WSDataGridNew
          key={`${search}|${paymentFilter}|${fromDate}|${toDate}|${sortOrder}`}
          id={`${id}-grid`}
          columns={tableColumns}
          data={tableRows}
          exportCsv={true}
          gridHeaderSx={{ backgroundColor: "whitesmoke", border: "1px solid whitesmoke" }}
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {visibleGroups.map((group) => {
            const expanded = showAllCards || !!expandedGroups[group.key];
            const visible = expanded ? group.orders : group.orders.slice(0, GROUP_PREVIEW);
            const style = getStatusStyle(group.label);
            return (
              <Box key={group.key} role="group" aria-label={`${group.label} orders`}>
                {/* Status group header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, px: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: style.color }} />
                  <Typography component="h3" sx={{ fontWeight: 700, color: COLORS.dark, fontSize: "0.95rem" }}>
                    {group.label}
                  </Typography>
                  <Chip size="small" label={group.orders.length} sx={{ height: 20, fontWeight: 700, bgcolor: style.bg, color: style.color }} />
                  <Divider sx={{ flexGrow: 1, borderColor: COLORS.border }} />
                </Box>

                <Grid container spacing={2}>
                  {visible.map((order) => (
                    <Grid item xs={12} sm={6} lg={4} key={order.bookingId}>
                      <OrderCard
                        order={order}
                        isNew={isNew(order.bookingId)}
                        onView={() => onView(order)}
                        onAssign={() => onAssign(order)}
                        onAccept={() => accept(order.bookingId)}
                        onSampleCollected={() => onSampleCollected(order)}
                        onSubmitReport={() => onSubmitReport(order)}
                        busy={busyIds.includes(order.bookingId)}
                      />
                    </Grid>
                  ))}
                </Grid>

                {!showAllCards && group.orders.length > GROUP_PREVIEW && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
                    <Button size="small" onClick={() => toggleGroup(group.key)} sx={{ color: COLORS.primary, textTransform: "none", fontWeight: 600 }}>
                      {group.key === "all"
                        ? expanded
                          ? "Show fewer orders"
                          : `Show all ${group.orders.length} orders`
                        : expanded
                          ? `Show fewer ${group.label.toLowerCase()} orders`
                          : `Show all ${group.orders.length} ${group.label.toLowerCase()} orders`}
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

interface LabOrdersListProps {
  // "active" = open order cards only; "history" = finished orders with filters
  mode?: "active" | "history";
}

export const LabOrdersList: React.FC<LabOrdersListProps> = React.memo(({ mode = "active" }) => {
  const navigate = useNavigate();
  const isHistory = mode === "history";

  // ?outcome=<filter> comes from the Lab Overview cards; "all" is the same as no filter
  const [searchParams, setSearchParams] = useSearchParams();
  const outcomeParam = searchParams.get("outcome");
  const outcomeFilter = isOutcomeFilter(outcomeParam) ? outcomeParam : null;
  const clearOutcome = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("outcome");
    setSearchParams(next, { replace: true });
  };

  const [labId, setLabId] = useState<string>("");
  const [orders, setOrders] = useState<LabBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [assignOrder, setAssignOrder] = useState<LabBooking | null>(null);
  const [sampleOrder, setSampleOrder] = useState<LabBooking | null>(null);
  const [busyIds, setBusyIds] = useState<string[]>([]);
  const { showMessage } = useSnackbar();

  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    setLabId(userId || "");
    if (!userId) setLoading(false);
  }, []);

  useEffect(() => {
    if (isHistory) {
      updateMainHeader("Order History");
      breadCrumbsStore.updateBreadCrumbs([{ "Order History": "/lab-orders/history" }]);
    } else {
      updateMainHeader("Today Orders");
      breadCrumbsStore.updateBreadCrumbs([{ "Today Orders": "/lab-orders/list" }]);
    }
  }, [isHistory]);

  // Refreshes can overlap (live pushes, notifications); only the latest request may update the list
  const latestRequest = useRef(0);
  // A visible load (spinner shown) is waiting; a newer request that replaces it finishes it
  const visibleLoadPending = useRef(false);
  // Test/package names by id; loaded once per lab (refreshes reuse them, and retry if empty)
  const serviceNames = useRef<{ labId: string; names: Map<string, string> } | null>(null);
  const loadServiceNames = useCallback(async (): Promise<Map<string, string>> => {
    if (serviceNames.current?.labId === labId && serviceNames.current.names.size) return serviceNames.current.names;
    const names = new Map<string, string>();
    try {
      const response: any = await http.get(`lab/LabService/bylab/${labId}`);
      const list: any[] = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      list.forEach((service) => {
        if (service?.id && service?.name) names.set(String(service.id).toLowerCase(), String(service.name));
      });
    } catch {
      // names stay as the bookings API sent them
    }
    serviceNames.current = { labId, names };
    return names;
  }, [labId]);

  const fetchOrders = useCallback(async (showLoading = true) => {
    if (!labId) return;
    const requestId = ++latestRequest.current;
    if (showLoading) {
      visibleLoadPending.current = true;
      setLoading(true);
      setError(false);
    }
    try {
      const [response, namesById]: [any, Map<string, string>] = await Promise.all([
        http.get(`lab/LabBooking/lab/${labId}`),
        loadServiceNames(),
      ]);
      if (requestId !== latestRequest.current) return;
      // http.get returns the error body (or nothing) instead of throwing when a request fails;
      // a failed request must not be shown as "no orders"
      if (!Array.isArray(response?.data)) throw new Error(response?.message || "Orders request failed");
      const apiData: LabBooking[] = fillServiceNames(response.data, namesById);
      setError(false);
      // Latest scheduled orders first
      apiData.sort((a, b) => new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime());
      // Orders not seen before blink until opened (only trust the list if the request worked)
      ingestOrders(
        apiData.map((o) => ({ id: o.bookingId, needsAttention: canAssignTechnician(o) })),
        true
      );
      setOrders(apiData);
    } catch (e) {
      console.error("Failed to fetch orders:", e);
      // A failed background refresh keeps the orders already shown
      if (requestId === latestRequest.current && visibleLoadPending.current) {
        setError(true);
        setOrders([]);
      }
    } finally {
      if (requestId === latestRequest.current) {
        visibleLoadPending.current = false;
        setLoading(false);
      }
    }
  }, [labId, loadServiceNames]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // New or updated orders pushed live by the backend
  useVendorOrdersHub(() => fetchOrders(false));

  const handleAssigned = () => {
    const bookingId = assignOrder?.bookingId;
    setAssignOrder(null);
    // Reflect the assignment immediately, then sync with the server
    setOrders((prev) => prev.map((o) => (o.bookingId === bookingId ? { ...o, status: "Assigned" } : o)));
    fetchOrders(false);
  };

  // Sample collected: saved after the vendor confirms; the card then offers Submit report
  const confirmSampleCollected = async () => {
    const order = sampleOrder;
    setSampleOrder(null);
    if (!order) return;
    const bookingId = order.bookingId;
    setBusyIds((prev) => [...prev, bookingId]);
    try {
      const response: any = await http.put("lab/LabBooking/update-status", {
        bookingId,
        status: SAMPLE_COLLECTED_STATUS,
      });
      if (!response?.success) throw new Error(response?.message || "Status update failed");
      setOrders((prev) => prev.map((o) => (o.bookingId === bookingId ? { ...o, status: SAMPLE_COLLECTED_STATUS } : o)));
      showMessage("Sample marked as collected", "success");
      fetchOrders(false);
    } catch (e: any) {
      console.error("Failed to mark sample collected:", e);
      showMessage(e?.response?.data?.message || e?.message || "Failed to update the order", "error");
    } finally {
      setBusyIds((prev) => prev.filter((id) => id !== bookingId));
    }
  };

  // Reports are uploaded per patient and test on the order page
  const submitReport = (order: LabBooking) => navigate(`/lab-orders/list/view/${order.bookingId}#lab-reports`);

  // Today Orders: today's orders waiting for a technician; Order History: assigned and finished orders.
  // A Lab Overview card filter (e.g. Delays, which are past-dated) replaces the today-only rule.
  // A new order is listed whatever its date until it is accepted.
  // Today's assigned, sample-collected and report-submitted orders are included too, so a card stays
  // on the page through every step.
  const { isNew: isNewOrder, arrivalRank, version: newOrderVersion } = useNewOrderHighlights();
  const pageOrders = useMemo(() => {
    const matching = outcomeFilter ? orders.filter((o) => matchesOutcome(o, outcomeFilter)) : orders;
    const todayKey = toDateKey(new Date().toISOString());
    // Order History: every order (ones without a technician show as Not Assigned)
    if (isHistory) return matching;
    const todayOrders = matching.filter((o) => {
      if (!isHistoryOrder(o)) {
        return outcomeFilter || toDateKey(o.scheduledDate) === todayKey || isNewOrder(o.bookingId);
      }
      // Assigned, sample collected or report submitted: kept on Today Orders for today's orders
      return !outcomeFilter && isTechnicianStepOrder(o) && toDateKey(o.scheduledDate) === todayKey;
    });
    // Today Orders: orders that came in while the app was open go first, the latest on top (they keep
    // that place after being accepted); then the rest, most recently placed first
    return todayOrders.sort(
      (a, b) =>
        arrivalRank(b.bookingId) - arrivalRank(a.bookingId) || getPlacedTime(b) - getPlacedTime(a)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, outcomeFilter, isHistory, newOrderVersion]);

  const viewOrder = (order: LabBooking) => navigate(`/lab-orders/list/view/${order.bookingId}`);

  return (
    <Box sx={{ mt: 1 }}>
      {/* Filter applied from a Lab Overview card */}
      {outcomeFilter && !loading && !error && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, px: 0.5 }}>
          <Typography sx={{ color: COLORS.muted, fontSize: "0.875rem" }}>Showing</Typography>
          <Chip
            size="small"
            label={OUTCOME_FILTER_LABELS[outcomeFilter]}
            onDelete={clearOutcome}
            sx={{ bgcolor: COLORS.light, color: COLORS.primary, fontWeight: 600 }}
          />
        </Box>
      )}

      {loading ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", textAlign: "center" }}>
          <Typography sx={{ color: COLORS.muted }}>Loading orders...</Typography>
        </Paper>
      ) : error ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", textAlign: "center" }}>
          <Typography sx={{ color: "#D32F2F" }}>Failed to load orders. Please try again.</Typography>
          <Button onClick={() => fetchOrders()} sx={{ mt: 1, color: COLORS.primary, textTransform: "none", fontWeight: 600 }}>
            Retry
          </Button>
        </Paper>
      ) : (
        <OrdersSection
          id={isHistory ? "order-history" : "open-orders"}
          title={isHistory ? "Order History" : "Today Orders"}
          orders={pageOrders}
          emptyText={
            outcomeFilter
              ? `No ${OUTCOME_FILTER_LABELS[outcomeFilter].toLowerCase()} orders right now.`
              : isHistory
                ? "Your lab's orders will appear here."
                : "No orders scheduled for today are waiting for a technician."
          }
          onView={viewOrder}
          onAssign={setAssignOrder}
          onSampleCollected={setSampleOrder}
          onSubmitReport={submitReport}
          busyIds={busyIds}
          showFilters={isHistory}
          showAllCards={isHistory}
          tableView={isHistory}
        />
      )}

      <AssignTechnicianDialog
        open={!!assignOrder}
        labId={labId}
        bookingId={assignOrder?.bookingId || ""}
        orderNumber={assignOrder ? getOrderNumber(assignOrder) : ""}
        patientName={assignOrder ? getPatientNames(assignOrder) : ""}
        onClose={() => setAssignOrder(null)}
        onAssigned={handleAssigned}
      />

      <Dialog open={!!sampleOrder} onClose={() => setSampleOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Sample collected?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: COLORS.dark, fontSize: "0.9rem" }}>
            Mark the sample for{" "}
            {sampleOrder && getOrderNumber(sampleOrder) ? `order #${getOrderNumber(sampleOrder)}` : "this order"}
            {sampleOrder ? ` (${getPatientNames(sampleOrder)})` : ""} as collected? You can then submit the report.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSampleOrder(null)} sx={{ color: COLORS.muted, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmSampleCollected}
            sx={{
              bgcolor: COLORS.primary,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              "&:hover": { bgcolor: COLORS.primary },
            }}
          >
            Yes, collected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export const LabOrderHistory: React.FC = () => <LabOrdersList mode="history" />;
